import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchInput from '@components/Common/SearchInput';

export interface PickerRow {
  id: string;
  title: string;
  /** Secondary line. Callers join their own parts; this component does not guess at a format. */
  subtitle?: string;
  /** Rendered after the subtitle and greys the row out. Use for "already submitted". */
  disabledReason?: string;
  /**
   * A third line, for a fact the chooser needs before deciding rather than after.
   *
   * Added for job assignment, where a row may already belong to another employer: picking it
   * takes that job and its applicants away from them. A reassignment nobody can see is how
   * applicant lists go missing, so the current owner is shown on the row itself.
   */
  meta?: React.ReactNode;
}

interface Props {
  open: boolean;
  title: string;
  searchPlaceholder: string;
  confirmLabel: string;
  /**
   * Rows for a query string. Called on open and on each debounced keystroke.
   *
   * Async because the job-seeker side must search server-side — that table is far too large to
   * hold in memory. A caller with a small fixed list can simply resolve immediately.
   */
  onSearch: (query: string) => Promise<PickerRow[]>;
  /** Required in single-select. Not called when `multiple` — use `onConfirmMany`. */
  onConfirm?: (id: string) => void | Promise<void>;
  onClose: () => void;
  submitting?: boolean;
  /**
   * Multi-select. Optional and off by default: both existing callers pick exactly one record and
   * must keep working untouched, so this is a second mode rather than a replacement.
   */
  multiple?: boolean;
  /** Required when `multiple`. `onConfirm` is not called in that mode. */
  onConfirmMany?: (ids: string[]) => void | Promise<void>;
  /** Ticked when the dialog opens. Used to default to the safe subset of a mixed list. */
  initialSelectedIds?: string[];
}

const DEBOUNCE_MS = 300;

/**
 * Pick one record from a searchable list.
 *
 * There were two of these facing opposite directions — pick a job for this candidate, and now pick
 * a candidate for this job — and the first was 55 lines of hand-rolled selectable `Box`es inline in
 * JobSeekerWorkspace, with no keyboard support and no listbox semantics. Copying it would have
 * meant fixing that twice, which in practice means fixing it never.
 *
 * The search is a caller-supplied async function rather than a query builder because the two sides
 * genuinely differ: jobs are a capped list filtered in memory, job seekers are a paginated table
 * that must be filtered by the server. Debounced here so neither caller has to remember to.
 */
const RecordPickerDialog: React.FC<Props> = ({
  open,
  title,
  searchPlaceholder,
  confirmLabel,
  onSearch,
  onConfirm,
  onClose,
  submitting = false,
  multiple = false,
  onConfirmMany,
  initialSelectedIds,
}) => {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<PickerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedId('');
      setSelectedIds(initialSelectedIds ?? []);
    }
    // initialSelectedIds is read only on open, so a caller recomputing the array on every render
    // cannot clear a selection the user has already made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /*
   * onSearch is held in a ref so this effect does not depend on its identity.
   *
   * It used to, with a comment asking callers to keep it stable. A caller then wrote an onSearch
   * that called setState with a fresh object AND listed that state in its own useCallback deps:
   * every search produced a new function, which re-ran this effect, which searched again. The
   * dialog sat on "Searching…" until the tab was closed, and the cause was two components away
   * from the symptom.
   *
   * A comment cannot enforce a contract that a caller breaks by accident. The ref removes the
   * dependency instead, so the search re-runs when the dialog opens or the query changes -- the
   * two things that should actually trigger it -- and an unstable caller costs nothing.
   */
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const next = await onSearchRef.current(query);
        // A slow earlier request must not overwrite a newer result.
        if (!cancelled) setRows(next);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query]);

  const selectable = useMemo(
    () => rows.filter((r) => !r.disabledReason),
    [rows],
  );

  /** Roving selection with the arrow keys, over the rows that can actually be chosen. */
  const move = (delta: number) => {
    if (selectable.length === 0) return;
    const at = selectable.findIndex((r) => r.id === selectedId);
    const next = at < 0 ? 0 : (at + delta + selectable.length) % selectable.length;
    setSelectedId(selectable[next].id);
  };

  const isTicked = (id: string) => selectedIds.includes(id);
  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  /* Select-all applies to what is on screen, which is what the user can see and reason about --
     not to rows a search has filtered out. */
  const allVisibleTicked =
    selectable.length > 0 && selectable.every((r) => selectedIds.includes(r.id));
  const confirmDisabled = submitting || (multiple ? selectedIds.length === 0 : !selectedId);
  const submit = () => {
    if (multiple) onConfirmMany?.(selectedIds);
    else onConfirm?.(selectedId);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {/* The same search box as every list page, rather than a plain field a few pixels taller
            on a different background. Arrow keys move the selection in the list below. */}
        <Box sx={{ mb: 1.5 }}>
          <SearchInput
            autoFocus
            placeholder={searchPlaceholder}
            value={query}
            onChange={setQuery}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
              if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
              if (e.key === 'Enter' && !confirmDisabled) submit();
            }}
          />
        </Box>

        <Box
          role="listbox"
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {loading && <Box sx={{ p: 1, color: 'var(--tx-low)' }}>Searching…</Box>}
          {!loading && rows.length === 0 && (
            <Box sx={{ p: 1, color: 'var(--tx-low)' }}>No matches.</Box>
          )}
          {!loading &&
            rows.map((row) => {
              const disabled = !!row.disabledReason;
              return (
                <Box
                  key={row.id}
                  role="option"
                  aria-selected={multiple ? isTicked(row.id) : selectedId === row.id}
                  aria-disabled={disabled}
                  tabIndex={-1}
                  onClick={() => {
                    if (disabled) return;
                    if (multiple) toggle(row.id);
                    else setSelectedId(row.id);
                  }}
                  sx={{
                    p: 1,
                    borderRadius: '8px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.55 : 1,
                    border:
                      (multiple ? isTicked(row.id) : selectedId === row.id)
                        ? '1px solid var(--acc)'
                        : '1px solid var(--line)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  {multiple && (
                    <Checkbox
                      checked={isTicked(row.id)}
                      disabled={disabled}
                      size="small"
                      sx={{ p: 0, mt: '2px' }}
                      // The row owns the click; the box must not fire a second toggle.
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => {
                        if (!disabled) toggle(row.id);
                      }}
                      slotProps={{ input: { 'aria-label': `Select ${row.title}` } }}
                    />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ fontSize: 'var(--font-size-sm)' }}>{row.title}</Box>
                    <Box sx={{ fontSize: 'var(--font-size-xs)', color: 'var(--tx-low)' }}>
                      {[row.subtitle, row.disabledReason].filter(Boolean).join(' · ')}
                    </Box>
                    {row.meta && (
                      <Box sx={{ fontSize: 'var(--font-size-xs)', color: 'var(--tx-low)' }}>
                        {row.meta}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      </DialogContent>
      <DialogActions>
        {multiple && (
          <>
            <Button
              onClick={() =>
                setSelectedIds(allVisibleTicked ? [] : selectable.map((r) => r.id))
              }
              disabled={selectable.length === 0 || submitting}
            >
              {allVisibleTicked ? 'Clear' : 'Select all'}
            </Button>
            <Box sx={{ flex: 1, fontSize: 'var(--font-size-xs)', color: 'var(--tx-low)' }}>
              {selectedIds.length} selected
            </Box>
          </>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={confirmDisabled} variant="contained" onClick={submit}>
          {submitting
            ? 'Submitting…'
            : multiple
              ? `${confirmLabel} (${selectedIds.length})`
              : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPickerDialog;
